import React, {useContext, useEffect, useRef, useState} from "react";
import {Alert, Button, Card, Form, Input, Modal, Select} from "antd";
import {Link} from "react-router-dom";
import {tokenService} from "../services/tokenService";
import {
    Organization,
    UserRole,
} from "../api-client";
import '../assets/css/Dentobook.css';
import {FormattedMessage, useIntl} from "react-intl";
import {CaretDownOutlined, CloseOutlined} from "@ant-design/icons";
import {isMobileOnly} from "react-device-detect";
import {allCountries, getCountry} from "../i18n/countries";
import {DentobookApplicationContext} from "../context/DentobookApplicationContext";
import {validatePass} from "../i18n/utils";
import {getUserApi} from "../api/apiInstances";
import logger from "../utils/logger";
import TurnstileWidget, {TurnstileHandle, TURNSTILE_ENABLED} from "../components/common/TurnstileWidget";
import { useTrackEvent } from "../hooks/useTrackEvent";
import { ErrorCode } from "../utils/analytics";

const Registration = () => {
    const intl = useIntl();
    const {locale} = useContext(DentobookApplicationContext);
    const userApi = getUserApi();
    const track = useTrackEvent();
    // Redirect logged-in users to their profile with the create-org modal open.
    // Use window.location.replace (full page reload) instead of React Router navigate()
    // so that Authentication re-mounts, detects a non-anonymous path, and fetches the
    // principal — otherwise the header menu stays empty because Authentication's one-time
    // useEffect already cleared principal when it saw the anonymous /register path.
    useEffect(() => {
        if (tokenService.isAuthenticated()) {
            window.location.replace('/profile?createOrg=1');
        }
    }, []);

    const [modalOpen, setModalOpen] = useState<boolean>(false);

    const [email, setEmail] = useState<string>('');
    const [password, setPassword] = useState<string>('');
    const [confirmPassword, setConfirmPassword] = useState<string>('');
    const [phone, setPhone] = useState<string>('');
    const [firstName, setFirstName] = useState<string>('');
    const [lastName, setlastName] = useState<string>('');
    const [organizationName, setOrganizationName] = useState<string>('');
    const [organizationCountry, setOrganizationCountry] = useState<string>('');
    const [errMsg, setErrMsg] = useState<React.ReactNode>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
    const turnstileRef = useRef<TurnstileHandle>(null);
    const submittingRef = useRef(false);
    const startedRef = useRef(false);

    const [form] = Form.useForm();

    useEffect(() => {
        const userLanguage = navigator.language || navigator.languages[0];

        let detectedCountry = '';

        if (userLanguage.toLowerCase().includes('bg')) {
            detectedCountry = 'BG';
        } else {
            fetch('https://ipapi.co/json/')
                .then(response => response.json())
                .then(data => {
                    const country = data?.country;  // Using optional chaining
                    if (country) {
                        detectedCountry = data.country;
                        form.setFieldsValue({country: detectedCountry});
                        setOrganizationCountry(detectedCountry);
                    }
                })
                .catch(err => {
                    logger.log('Failed to detect country', err);
                });
        }

        if (detectedCountry) {
            form.setFieldsValue({country: detectedCountry});
            setOrganizationCountry(detectedCountry);
        }
    }, []);

    useEffect(() => {
        const containerDiv = document.getElementById('registration-form-container');
        if (containerDiv) {
            containerDiv.scrollTo(0, 0);
        }
    }, [errMsg]);

    const onFinish = async () => {
        // Synchronous ref guard — prevents re-entrant calls from double-click
        // or Enter key repeat before React re-renders the disabled button.
        if (submittingRef.current) {
            return;
        }

        if (TURNSTILE_ENABLED && !turnstileToken) {
            setErrMsg(intl.formatMessage({id: 'complete_bot_verification'}));
            return;
        }

        if (password !== confirmPassword) {
            setErrMsg(intl.formatMessage({id: 'passwords_do_not_match'}));
            return;
        }
        if (!validatePass(password)) {
            setErrMsg(intl.formatMessage({id: 'password_requirements_hint'}));
            return;
        }

        // All client-side validation passed — record the submission attempt.
        track('registration_submitted', { source: 'registration_form', step: 'submit' });

        // Lock immediately (synchronous) — no async gap before this point.
        submittingRef.current = true;
        setLoading(true);

        // Capture and clear the token so it cannot be reused by a second call.
        const capturedToken = turnstileToken;
        setTurnstileToken(null);

        try {
            // Prepare registration data for transactional endpoint
            const registrationData = {
                email: email,
                password: password,
                confirmPassword: confirmPassword,
                firstName: firstName,
                lastName: lastName,
                phone: phone,
                role: UserRole.ClinicAdmin,
                organizationName: organizationName,
                organizationEmail: email,
                organizationPhone: phone || null,
                organizationCountry: organizationCountry,
                organizationCurrency: getCountry(organizationCountry)!.currency_code,
                organizationAddress: " ",
                turnstileToken: capturedToken || undefined,
            };

            // Call single transactional endpoint
            await userApi.selfRegisterUser(registrationData);

            track('user_registered', { country_code: organizationCountry, source: 'registration_form' });

            // Show success modal - user will need to login and accept terms
            // Note: Not auto-logging in because user needs to see terms modal on first login
            setModalOpen(true);
        } catch (err: any) {
            // Handle backend validation errors
            const errorDetail = err?.response?.data?.detail || err.message;
            const statusCode = err?.response?.status;

            // Map backend error → normalized ErrorCode. Never send free-form strings.
            let registrationErrorCode: ErrorCode;
            if (statusCode === 422) {
                registrationErrorCode = ErrorCode.VALIDATION_ERROR;
            } else if (typeof errorDetail === 'string') {
                if (errorDetail.includes('Organization with this name already exists')) {
                    registrationErrorCode = ErrorCode.DUPLICATE_ORG;
                } else if (errorDetail.includes('email already exists') || errorDetail.includes('Email is already taken')) {
                    registrationErrorCode = ErrorCode.DUPLICATE_EMAIL;
                } else if (errorDetail.includes('Passwords do not match')) {
                    registrationErrorCode = ErrorCode.PASSWORD_MISMATCH;
                } else {
                    registrationErrorCode = ErrorCode.UNKNOWN;
                }
            } else {
                registrationErrorCode = ErrorCode.UNKNOWN;
            }
            track('registration_failed', { error_code: registrationErrorCode, reason: registrationErrorCode, http_status: statusCode ?? 0, source: 'registration_form', step: 'error' });

            // Handle 422 validation errors (Pydantic validation)
            if (statusCode === 422 && Array.isArray(errorDetail)) {
                const validationErrors = errorDetail.map((error: any) => {
                    const field = error.loc?.[error.loc.length - 1] || 'field';
                    const msg = error.msg || error.ctx?.reason || 'Invalid value';
                    return `${field}: ${msg}`;
                }).join('\n');
                setErrMsg(validationErrors);
            } else if (typeof errorDetail === 'string') {
                if (errorDetail.includes("Organization with this name already exists")) {
                    setErrMsg(intl.formatMessage({id: 'organization_already_exists'}));
                } else if (errorDetail.includes("email already exists") || errorDetail.includes("Email is already taken")) {
                    setErrMsg(
                        <FormattedMessage
                            id="email_already_taken_register"
                            values={{
                                link: <Link to="/profile?createOrg=1"><FormattedMessage id="my_profile"/></Link>,
                            }}
                        />
                    );
                } else if (errorDetail.includes("Passwords do not match")) {
                    setErrMsg(intl.formatMessage({id: 'passwords_do_not_match'}));
                } else {
                    setErrMsg(errorDetail || intl.formatMessage({id: 'unexpected_error'}));
                }
            } else {
                setErrMsg(intl.formatMessage({id: 'unexpected_error'}));
            }
            // Reset widget so user gets a fresh token for retry
            turnstileRef.current?.reset();
        } finally {
            submittingRef.current = false;
            setLoading(false);
        }
    };

    const handleConfirm = () => {
        // Redirect to home/dashboard after registration
        window.location.href = '/';
    }

    const footer = <div className="modal-btn-wrapper">
        <Button
            style={isMobileOnly ? {width: '70vw'} : {}}
            className="modal-btn" type="primary" onClick={handleConfirm}
        >
            <FormattedMessage id="close" />
        </Button>
    </div>;

    return (
        <>
            <Modal
                transitionName=""
                footer={footer}
                className="dentobook-modal"
                title={intl.formatMessage({ id: 'successful_registration_title' })}
                open={modalOpen}>
                <h3 className={"dentobook-content font-weight-500 font-24 text-align-center " + (isMobileOnly ? "mt-112" : "")}>
                    <FormattedMessage id="registration_success_message" />
                </h3>
            </Modal>
            <div
                style={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    maxHeight: "100vh",
                    overflow: "hidden",
                }}
            >
                <div style={{ marginBottom: "20px", textAlign: "center" }}>
                    <a href="https://dentobook.com/en/" target="_blank" rel="noopener noreferrer">
                        <img
                            alt="logo"
                            className="dentobook-logo"
                            src={require("../assets/logo.png")}
                            style={{
                                width: "420px",
                                height: "104px",
                                padding: "13px 10px 13.65px 10px",
                            }}
                        />
                    </a>
                </div>
                <Card id="registration-form-container" style={{
                    width: 480,
                    padding: "40px",
                    borderRadius: "30px",
                    boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)"
                }}>
                    <Form form={form} layout="vertical"
                          onFinish={onFinish}
                          onValuesChange={() => {
                              if (startedRef.current) return;
                              startedRef.current = true;
                              // form_started fires once on first user interaction — replaces
                              // unreliable GA4 automatic form_start in SPAs.
                              track('form_started', { source: 'registration_form' });
                              track('registration_started', { source: 'registration_form', step: 'start' });
                          }}
                          style={{textAlign: "left"}}>
                        {errMsg ? <Alert message={errMsg}
                                         type="error"
                                         className="mb-24"
                                         closeIcon={<CloseOutlined/>}
                                         onClose={() => setErrMsg(null)}
                                         showIcon
                                         onClick={() => setErrMsg(null)}/> : null}
                        <Form.Item
                            className={"dentobook-form-item dentobook-simple-input"}
                            label={<FormattedMessage id="email" />}
                            name="email"
                            rules={[{required: true, message: intl.formatMessage({ id: 'email_required_error' })}, {
                                type: "email",
                                message: intl.formatMessage({ id: 'invalid_email' })
                            }]}
                        >
                            <Input style={{borderRadius: "12px", height: "48px"}}
                                   autoComplete={"new-email"}
                                   value={email}
                                   onChange={e => setEmail(e.target.value)}/>
                        </Form.Item>

                        <Form.Item
                            className={"dentobook-form-item dentobook-simple-input"}
                            label={<FormattedMessage id="password" />}
                            name="password"
                            rules={[{required: true, message: intl.formatMessage({ id: 'password_required_error' })}]}
                        >
                            <Input type={"password"} style={{borderRadius: "12px", height: "48px"}}
                                   autoComplete={"new-password"}
                                   value={password}
                                   onChange={e => setPassword(e.target.value)}/>
                        </Form.Item>
                        <Form.Item
                            className={"dentobook-form-item dentobook-simple-input"}
                            label={<FormattedMessage id="confirm_password" />}
                            name="confirmPassword"
                            rules={[{required: true, message: intl.formatMessage({ id: 'password_required_error' })}]}
                        >
                            <Input type={"password"} style={{borderRadius: "12px", height: "48px"}}
                                   value={confirmPassword}
                                   onChange={e => setConfirmPassword(e.target.value)}/>
                        </Form.Item>

                        <Form.Item
                            className={"dentobook-form-item dentobook-simple-input"}
                            label={<FormattedMessage id="phone_number" />}
                            name="phone"
                            rules={[
                                {required: true, message: intl.formatMessage({ id: 'phone_required' })},
                                {
                                    pattern: /^\+[1-9]\d{6,14}$/,
                                    message: intl.formatMessage({ id: 'phone_international_required' })
                                }
                            ]}
                            extra={<span style={{fontSize: 12, color: '#888'}}><FormattedMessage id="phone_format_hint" /></span>}
                        >
                            <Input style={{borderRadius: "12px", height: "48px"}} value={phone}
                                   placeholder="+359888123456"
                                   onChange={e => setPhone(e.target.value)}/>
                        </Form.Item>

                        <Form.Item
                            className="dentobook-form-item dentobook-simple-input"
                            label={<FormattedMessage id="first_name" />}
                            name="firstName"
                            rules={[
                                { required: true, message: intl.formatMessage({ id: 'first_name_required' }) },
                                {
                                    pattern: /^[\p{L}]+$/u,
                                    message: intl.formatMessage({ id: 'first_name_letters_only' })
                                },
                            ]}
                        >
                            <Input
                                style={{ borderRadius: "12px", height: "48px" }}
                                value={firstName}
                                onChange={e => {
                                    setFirstName(e.target.value);
                                    form.setFieldsValue({ firstName: e.target.value });
                                }}
                            />
                        </Form.Item>

                        <Form.Item
                            className="dentobook-form-item dentobook-simple-input"
                            label={<FormattedMessage id="last_name" />}
                            name="lastName"
                            rules={[
                                { required: true, message: intl.formatMessage({ id: 'last_name_required' }) },
                                {
                                    pattern: /^[\p{L}]+$/u,
                                    message: intl.formatMessage({ id: 'last_name_letters_only' })
                                },
                            ]}
                        >
                            <Input
                                style={{ borderRadius: "12px", height: "48px" }}
                                value={lastName}
                                onChange={e => {
                                    setlastName(e.target.value);
                                    form.setFieldsValue({ lastName: e.target.value });
                                }}
                            />
                        </Form.Item>

                        <Form.Item
                            className={"dentobook-form-item dentobook-simple-input single-select mb-40"}
                            label={<FormattedMessage id="country" />}
                            name="country"
                            initialValue={organizationCountry}
                            rules={[{required: true, message: intl.formatMessage({ id: 'country_required' })}]}
                        >
                            <Select
                                showSearch
                                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                // @ts-ignore
                                autoComplete="new-state"
                                suffixIcon={<CaretDownOutlined/>}
                                className="dentobook-input"
                                value={organizationCountry}
                                onChange={e => setOrganizationCountry(e)}
                                filterOption={(input, option) =>
                                    String(option?.children).toLowerCase().includes(input.toLowerCase())
                                }
                                placeholder={intl.formatMessage({id: 'search'})}
                            >
                                {allCountries.map(countryItem => (
                                    <Select.Option key={countryItem.code}
                                                   value={countryItem.code}>
                                        {countryItem.name_en}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                        <Form.Item
                            className={"dentobook-form-item dentobook-simple-input"}
                            label={<FormattedMessage id="organization_name" />}
                            name="organization"
                            rules={[{required: true, message: intl.formatMessage({ id: 'clinic_name_required' })},
                                {max: 250, message: intl.formatMessage({ id: 'clinic_name_max_length' })}]}
                        >
                            <Input style={{borderRadius: "12px", height: "48px"}} value={organizationName}
                                   onChange={e => setOrganizationName(e.target.value)}
                            />
                        </Form.Item>

                        <TurnstileWidget
                            ref={turnstileRef}
                            onVerify={setTurnstileToken}
                            onExpire={() => setTurnstileToken(null)}
                        />

                        <div className="dentobook-register-btn">
                            <Form.Item className={"dentobook-form-item dentobook-simple-input"}>
                                <Button
                                    className="dentobook-btn-primary dentobook-btn-medium"
                                    type="primary"
                                    htmlType="submit"
                                    disabled={loading || (TURNSTILE_ENABLED && !turnstileToken)}
                                    style={{
                                        width: "100%",
                                        height: "48px",
                                    }}
                                >
                                    <FormattedMessage id="create_account" />
                                </Button>
                            </Form.Item>
                        </div>

                        <div style={{ textAlign: "center", marginTop: "16px" }}>
                            <span style={{ color: "#666" }}><FormattedMessage id="already_have_account" /> </span>
                            <Link to="/login" style={{ color: "#1890ff", fontWeight: 500 }}>
                                <FormattedMessage id="login_here" />
                            </Link>
                        </div>
                    </Form>
                </Card>
            </div>
        </>
    );
};

export default Registration;
